import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

export type TripCartItemKind = "flight" | "hotel" | "car" | "taxi" | "holiday" | "tour" | "insurance";

export interface TripCartItem {
  id: string;
  kind: TripCartItemKind;
  title: string;
  subtitle?: string;
  price: number;
  currency: string;
  qty: number;
  meta?: Record<string, any>;
  image?: string;
  addedAt: number;
}

interface CtxValue {
  items: TripCartItem[];
  total: number;
  count: number;
  add: (it: Omit<TripCartItem, "addedAt" | "qty"> & { qty?: number }) => void;
  remove: (id: string) => void;
  clear: () => void;
  setQty: (id: string, qty: number) => void;
}

const STORAGE_KEY = "gloogum_travel_cart_v1";
const Ctx = createContext<CtxValue | null>(null);

export function TripCartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<TripCartItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const add: CtxValue["add"] = useCallback((it) => {
    setItems((prev) => {
      if (prev.some(p => p.id === it.id)) return prev;
      return [...prev, { ...it, qty: it.qty ?? 1, addedAt: Date.now() }];
    });
  }, []);
  const remove = useCallback((id: string) => setItems(p => p.filter(i => i.id !== id)), []);
  const clear = useCallback(() => setItems([]), []);
  const setQty = useCallback((id: string, qty: number) => setItems(p => p.map(i => i.id === id ? { ...i, qty: Math.max(1, qty) } : i)), []);

  const total = items.reduce((s, i) => s + i.price * i.qty, 0);
  return <Ctx.Provider value={{ items, total, count: items.length, add, remove, clear, setQty }}>{children}</Ctx.Provider>;
}

export function useTripCart() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTripCart must be used within TripCartProvider");
  return v;
}